import React, { useMemo } from 'react'
import {
  Star,
  AlertCircle,
  Image,
  HelpCircle,
  Copy,
  Tag,
  MapPin,
} from 'react-feather'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { format, sub } from 'date-fns'
import { ptBR, enUS } from 'date-fns/locale'
import { FormattedMessage, useIntl } from 'react-intl'
import { NOTIFICATION_TYPES } from '~/constants/notifications'
import { Button, Col, Row } from 'reactstrap'
import { Show } from '~/components/commons'
import { NotificationPageContext } from '..'
import { SUPER_ADMIN } from '~/constants/roles'

function NotificationCard({
  item: { type, publishedAt, location, messageData, id },
}) {
  const { fetchDataAndOpenModal, isFetching, setType } = React.useContext(
    NotificationPageContext
  )
  const projects = useSelector(state => state.user?.profile?.projects || [])

  const notificationIcon = type => {
    switch (type) {
      case 'NEW_REVIEW' || 'UPDATED_REVIEW':
        return {
          color: '#EDC223',
          icon: <Star size={20} color="#EDC223" />,
        }

      case 'GOOGLE_UPDATE':
        return {
          color: '#FFA84C',
          icon: <AlertCircle size={20} color="#FFA84C" />,
        }
      case 'NEW_QUESTION' ||
        'UPDATED_QUESTION' ||
        'NEW_ANSWER' ||
        'UPDATED_ANSWER':
        return {
          color: '#0091FF',
          icon: <HelpCircle size={20} color="#0091FF" />,
        }
      case 'UPDATED_LOCATION_STATE':
        return {
          color: '#ED5F5F',
          icon: <Copy size={20} color="#ED5F5F" />,
        }
      case 'NEW_CUSTOMER_MEDIA':
        return {
          color: '#8D8D8D',
          icon: <Image size={20} color="#8D8D8D" />,
        }
      default:
        return {
          color: '#7367F0',
          icon: <Star size={20} color="#7367F0" />,
        }
    }
  }

  const projectName = locationName => {
    const gmbAccountId = locationName.split('/')[1]
    if (projects.length > 1) {
      return (
        projects.find(project => project.gmbAccountId === gmbAccountId)
          ?.name || <FormattedMessage id="Project name" />
      )
    } else {
      return projects[0].name
    }
  }

  const address = location => {
    const { addressLines, locality, administrativeArea } =
      location?.data?.storefrontAddress || {}

    return `${addressLines?.join(', ') ?? ''} - ${
      locality ? locality + '/' : ''
    }${administrativeArea ?? ''}`
  }

  const intl = useIntl()
  const durationAgo = publishedDate => {
    const date = new Date(publishedDate)
    const tzDate = sub(date, { hours: 3 })
    return format(tzDate, 'PPPPpp', {
      locale: intl.locale === 'br' ? ptBR : enUS,
    })
  }

  const userProfile = useSelector(state => state.user?.profile)
  const isAppAdmin = userProfile?.role === SUPER_ADMIN

  const canAnswerOrModify = useMemo(() => {
    return (
      isAppAdmin &&
      [
        'NEW_QUESTION',
        'UPDATED_QUESTION',
        'NEW_ANSWER',
        'UPDATED_ANSWER',
        'NEW_REVIEW',
        'UPDATED_REVIEW',
        'NEW_CUSTOMER_MEDIA',
      ].includes(type)
    )
  }, [isAppAdmin, type])

  return (
    <Row className="d-flex align-items-center mb-2">
      <Col md={12} className="d-flex align-items-center">
        <IconContainer color={notificationIcon(type).color}>
          {notificationIcon(type).icon}
        </IconContainer>
        <div className="w-100">
          <p className="mb-0">
            <b>
              <FormattedMessage id={NOTIFICATION_TYPES[type]} />
            </b>
            {` (${durationAgo(publishedAt)})`}
          </p>
          <Row>
            <Col md={canAnswerOrModify ? 9 : 12}>
              <Row className="d-flex">
                <Col md={12} className="mr-2 mb-0">
                  {location?.name}
                </Col>
                <Col md={12}>
                  <Tag size={14} style={{ marginRight: '8px' }} />
                  {projectName(
                    messageData?.locationName || messageData?.location
                  )}
                </Col>
                <Col md={12}>
                  <MapPin size={14} style={{ marginRight: '8px' }} />
                  {address(location)}
                </Col>
              </Row>
            </Col>
            <Show condition={canAnswerOrModify}>
              <Col md="3">
                <Button
                  color="primary"
                  disabled={isFetching}
                  onClick={() => {
                    setType(type)
                    fetchDataAndOpenModal(id, type)
                  }}
                >
                  {type === 'NEW_CUSTOMER_MEDIA' ? 'Visualizar' : 'Responder'}
                </Button>
              </Col>
            </Show>
          </Row>
        </div>
      </Col>
    </Row>
  )
}

const IconContainer = styled.div`
  height: 30px;
  width: 30px;
  border: 1px solid ${props => props.color ?? '#8D8D8D'};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 15px;
`

export default NotificationCard
